import React, { useEffect, useState } from "react";
import { useFilters, useGlobalFilter, useSortBy, useTable } from "react-table";
import { getFromLocalStorage, saveToLocalStorage } from "../../../utils/Util";
import { BalancePartnerSelector } from "../../domain/balance/BalancePartnerSelector";
import {
  booleanFilter,
  dateFilter,
  DefaultColumnFilter,
  fuzzyTextFilterFn,
} from "../filters/Filters";
import { ActionPanel } from "./ActionPanel";
import { FilterPanel } from "./FilterPanel";
import { TableBody } from "./TableBody";
import { TableHeader } from "./TableHeader";
import InfiniteScroll from "react-infinite-scroll-component";
import { TableLoadingIndicator } from "./TableLoadingIndicator";

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

export function TableContainer({
  columns,
  data,
  onFetchData,
  tableName,
  needAggregate,
  aggregatePanel,
  newObjectLabel,
  dialogContent,
  refreshState,
  getBalancePartnerOptions,
  onBalancePartnerChange,
  csvOnChangeHandler,
  csvOnClickHandler,
  cookieObjects,
  dialogSize,
  triggerMatcher,
  syncInvoices,
  isLoading = false,
}) {
  const filterTypes = React.useMemo(
    () => ({
      dateText: dateFilter,
      booleanText: booleanFilter,
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
      minWidth: 30,
      width: 150,
      maxWidth: 400,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    setGlobalFilter,
    setHiddenColumns,
    setAllFilters,
  } = useTable(
    {
      columns,
      data,
      hiddenColumns: columns
        .filter((column) => !column.show)
        .map((column) => column.id),
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      autoResetGlobalFilter: false,
      autoResetFilters: false,
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy
  );

  useEffect(() => {
    setHiddenColumns(
      columns.filter((column) => !column.show).map((column) => column.id)
    );
  }, [columns, setHiddenColumns]);

  const incl = (obj, id) => {
    let incl = false;
    if (obj) {
      /* eslint-disable array-callback-return */
      obj.map((o) => {
        if (o.value === id) {
          incl = true;
        }
      });
    } else {
      return false;
    }
    return incl;
  };

  const [activeFilters, setActiveFilters] = useState([]);
  const setActiveFiltersFn = (filters) => {
    console.log(activeFilters);
    setActiveFilters(filters);
    saveToLocalStorage(tableName, "activeFilters", filters);
  };
  const getActiveFilters = () => {
    const filters = getFromLocalStorage(tableName, "activeFilters");
    return filters;
  };

  const [activeColumns, setActiveColumns] = useState([]);
  const setActiveColumnsFn = (columns) => {
    console.log(activeColumns);
    setActiveColumns(columns);
    saveToLocalStorage(tableName, "activeColumns", columns);
  };

  const getActiveColumns = () => {
    const columns = getFromLocalStorage(tableName, "activeColumns");
    if (columns) {
      allColumns.map((column) => {
        const state = column.getToggleHiddenProps();
        if (typeof column.Header === "string") {
          if (incl(columns, column.id) && false === state.checked) {
            column.toggleHidden();
          }
          if (!incl(columns, column.id) && true === state.checked) {
            column.toggleHidden();
          }
        }
      });
      return columns;
    } else {
      const res = [];
      allColumns.map((column) => {
        const state = column.getToggleHiddenProps();
        if (state.checked && typeof column.Header === "string") {
          const header =
            column.parent && typeof column.parent.Header === "string"
              ? column.parent.Header + " " + column.Header
              : column.Header;
          const obj = {
            value: column.id,
            label: header,
          };
          res.push(obj);
        }
      });
      return res;
    }
  };

  const [displayedData, setDisplayedData] = useState([]);
  useEffect(() => {
    setDisplayedData(rows.slice(0, 50));
  }, [rows]);

  const fetchMoreData = () => {
    setDisplayedData(rows.slice(0, displayedData.length + 50));
  };

  const getAllData = () => {
    setDisplayedData(rows);
  };

  const hasMore = () => {
    if (rows.length > displayedData.length) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <div className="content-header">
        <div className="flex-container">
          <div className="title-actions">
            <div className="title-data">
              <h1>{tableName}</h1>
              {needAggregate ? aggregatePanel(displayedData) : <></>}
              {getBalancePartnerOptions ? (
                <BalancePartnerSelector
                  getOptions={getBalancePartnerOptions}
                  onChange={onBalancePartnerChange}
                />
              ) : (
                <></>
              )}
            </div>
            <div className="actions">
              <ActionPanel
                headerGroups={headerGroups}
                setAllFilters={setAllFilters}
                setGlobalFilter={setGlobalFilter}
                diff={data.length - displayedData.length}
                columns={columns}
                newObjectLabel={newObjectLabel}
                dialogContent={dialogContent}
                refreshState={refreshState}
                getBalancePartnerOptions={getBalancePartnerOptions}
                onBalancePartnerChange={onBalancePartnerChange}
                csvOnChangeHandler={csvOnChangeHandler}
                csvOnClickHandler={csvOnClickHandler}
                triggerMatcher={triggerMatcher}
                setActiveFilters={setActiveFiltersFn}
                activeFilters={getActiveFilters()}
                setActiveColumns={setActiveColumnsFn}
                activeColumns={getActiveColumns()}
                tableName={tableName}
                allColumns={allColumns}
                cookieObjects={cookieObjects}
                dialogSize={dialogSize}
                getAllData={getAllData}
                syncInvoices={syncInvoices}
              />
            </div>
          </div>
          <div className="filter-panel">
            <FilterPanel
              headerGroups={headerGroups}
              activeFilters={getActiveFilters()}
              tableName={tableName}
            />
          </div>
        </div>
      </div>
      <div className="content" aria-busy={isLoading}>
        {isLoading ? (
          <TableLoadingIndicator />
        ) : (
          <>
            <InfiniteScroll
              style={{ overflow: "o", height: "n" }}
              dataLength={displayedData.length}
              next={fetchMoreData}
              hasMore={hasMore()}
              loader={<h4>Betöltés...</h4>}
            >
              <table className="bb-table" {...getTableProps()}>
                <TableHeader headerGroups={headerGroups} allColumns={allColumns} />
                <TableBody
                  headerGroups={headerGroups}
                  allColumns={allColumns}
                  rows={displayedData}
                  getTableBodyProps={getTableBodyProps}
                  prepareRow={prepareRow}
                />
              </table>
            </InfiniteScroll>
            {displayedData.length === 0 && <div>Nincs találat</div>}
          </>
        )}
      </div>
    </>
  );
}
